import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GlobeIcon, FileTextIcon, ShieldCheckIcon, UsersIcon, ArrowRightIcon } from 'lucide-react';
import { ConstellationGraphic } from './ConstellationGraphic';
export function Hero() {
  const services = [{
    icon: GlobeIcon,
    label: 'Global Sourcing'
  }, {
    icon: FileTextIcon,
    label: 'Tender Support'
  }, {
    icon: ShieldCheckIcon,
    label: 'Supplier Verification'
  }, {
    icon: UsersIcon,
    label: 'Contract Liaison'
  }];
  const stats = [{
    icon: GlobeIcon,
    value: '30+',
    label: 'Countries Served'
  }, {
    icon: UsersIcon,
    value: '500+',
    label: 'Verified Suppliers'
  }, {
    icon: ShieldCheckIcon,
    value: '5',
    label: 'Continents'
  }];
  return <section className="relative min-h-screen pt-32 pb-20 px-6 overflow-hidden" data-id="element-147">
      <div className="max-w-7xl mx-auto" data-id="element-148">
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-149">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-150">
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-6" data-id="element-151">
              <div className="w-12 h-px bg-gold" data-id="element-152"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-153">
                Global Procurement & Strategic Sourcing
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-semibold leading-tight mb-6" data-id="element-154">
              <span className="text-white" data-id="element-155">
                Connecting Global
                <br data-id="element-156" />
                Suppliers to
              </span>{' '}
              <span className="text-slate-300" data-id="element-157">
                International
                <br data-id="element-158" />
                Procurement Opportunities
              </span>
            </h1>

            {/* Subcopy */}
            <p className="text-slate-400 text-lg leading-relaxed mb-8 max-w-xl" data-id="element-159">
              We are an independent procurement consultancy facilitating
              sourcing, supplier verification, and tender coordination for
              large-scale institutional projects worldwide. We connect
              manufacturers, exporters, and distributors with procurement
              opportunities relating to multilateral, government-backed, and
              African Union (AU)-funded programmes across Africa, Asia, the
              Middle East, and beyond.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-12" data-id="element-160">
              <Link to="/supplier-registration" className="bg-gold hover:bg-gold-dark text-navy-dark px-8 py-3.5 rounded-md font-semibold text-sm transition-colors flex items-center gap-2" data-id="element-161">
                Register as a Supplier
                <ArrowRightIcon size={16} data-id="element-162" />
              </Link>
              <Link to="/services" className="border-2 border-slate-700 hover:border-gold text-white px-8 py-3.5 rounded-md font-semibold text-sm transition-colors" data-id="element-163">
                Our Services
              </Link>
            </div>

            {/* Service Pills */}
            <div className="flex flex-wrap gap-3 mb-12" data-id="element-164">
              {services.map((service, i) => <motion.div key={service.label} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.3,
              delay: 0.8 + i * 0.1
            }} className="flex items-center gap-2 bg-navy-light border border-slate-800 px-4 py-2 rounded-full" data-id="element-165">
                  <service.icon size={16} className="text-gold" data-id="element-166" />
                  <span className="text-slate-300 text-sm font-medium" data-id="element-167">
                    {service.label}
                  </span>
                </motion.div>)}
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-slate-800 mb-8" data-id="element-168"></div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6" data-id="element-169">
              {stats.map((stat, i) => <motion.div key={stat.label} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 1.2 + i * 0.1
            }} className="text-center" data-id="element-170">
                  <div className="w-10 h-10 border border-gold/30 rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-171">
                    <stat.icon size={20} className="text-gold" data-id="element-172" />
                  </div>
                  <div className="text-2xl font-bold text-white mb-1" data-id="element-173">
                    {stat.value}
                  </div>
                  <div className="text-slate-400 text-sm font-medium" data-id="element-174">
                    {stat.label}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Right Content - Constellation */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="relative hidden lg:block" data-id="element-175">
            <div className="relative w-full h-[500px]" data-id="element-176">
              <ConstellationGraphic data-id="element-177" />

              {/* Floating Card */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 1.5
            }} className="absolute bottom-12 right-0 max-w-sm bg-navy-light/90 backdrop-blur-sm border-l-4 border-gold p-6 rounded-lg" data-id="element-178">
                <h3 className="text-white font-semibold mb-2" data-id="element-179">
                  Active Across 5 Continents
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-180">
                  Facilitating procurement worldwide with a global network of
                  verified manufacturers, exporters, and distributors, with
                  strategic focus on emerging and high-growth markets.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}